/**
 * Hablamos Teacher Dashboard - Main App Component
 * B2B: language-school teachers monitor their students' progress.
 * Auth: Firebase login + users/{uid}.userType === 'teacher' (or isSuperAdmin).
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import {
  FiUsers, FiBarChart2, FiTarget, FiLogOut, FiMenu, FiX, FiRefreshCw,
} from 'react-icons/fi';
import teacherAPI from './services/api';

import Roster from './pages/Roster';
import StudentDetail from './pages/StudentDetail';
import SchoolOverview from './pages/SchoolOverview';
import ClassInsights from './pages/ClassInsights';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Allow teachers OR owner (superAdmin/admin) for demos/testing
function isAllowed(data) {
  if (!data) return false;
  return data.userType === 'teacher' || data.isSuperAdmin === true || data.role === 'admin';
}

// ============================================
// LOGIN PAGE
// ============================================
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists() || !isAllowed(userDoc.data())) {
        await signOut(auth);
        setError('Access denied. This portal is for school teachers.');
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();
      teacherAPI.setToken(token);
      onLogin(user, userDoc.data());
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Login failed. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Hablamos for Schools</h1>
          <p>Sign in to view your students' progress</p>
        </div>

        {error && <div className="login-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '12px' }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================
// SIDEBAR
// ============================================
function Sidebar({ user, school, onLogout, isOpen, onClose }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut(auth);
    onLogout();
    navigate('/');
  };

  const navItems = [
    { path: '/', icon: FiUsers, label: 'Students', end: true },
    { path: '/teach-next', icon: FiTarget, label: 'Teach Next' },
    { path: '/school', icon: FiBarChart2, label: 'School Overview' },
  ];

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`admin-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">H</div>
            <div className="sidebar-logo-text">
              Hablamos <span>Schools</span>
            </div>
          </div>
          <button className="sidebar-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">
              {school?.name || 'My School'}
            </div>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={onClose}
                end={item.end}
              >
                <span className="nav-item-icon"><item.icon /></span>
                <span className="nav-item-text">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <span className="admin-email">{user?.email}</span>
          </div>
          <button className="btn btn-secondary logout-btn" onClick={handleLogout}>
            <FiLogOut /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}

// ============================================
// HEADER
// ============================================
function Header({ title, onMenuClick, onRefresh, loading }) {
  return (
    <header className="admin-header">
      <div className="header-left">
        <button className="menu-toggle" onClick={onMenuClick}>
          <FiMenu />
        </button>
        <h1>{title}</h1>
      </div>
      <div className="header-actions">
        <button className="btn btn-secondary" onClick={onRefresh} disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>
    </header>
  );
}

// ============================================
// MAIN LAYOUT
// ============================================
function MainLayout({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('Students');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);

  useEffect(() => {
    teacherAPI.getMe()
      .then((d) => setSchool(d.school))
      .catch((e) => console.error('getMe failed:', e.message));
  }, [refreshKey]);

  const handleRefresh = () => {
    setLoading(true);
    setRefreshKey((p) => p + 1);
    setTimeout(() => setLoading(false), 800);
  };

  return (
    <div className="admin-layout">
      <Sidebar
        user={user}
        school={school}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="admin-main">
        <Header
          title={pageTitle}
          onMenuClick={() => setSidebarOpen(true)}
          onRefresh={handleRefresh}
          loading={loading}
        />
        <div className="admin-content">
          <Routes>
            <Route path="/" element={<Roster setPageTitle={setPageTitle} refreshKey={refreshKey} />} />
            <Route path="/teach-next" element={<ClassInsights setPageTitle={setPageTitle} refreshKey={refreshKey} />} />
            <Route path="/student/:uid" element={<StudentDetail setPageTitle={setPageTitle} />} />
            <Route path="/school" element={<SchoolOverview setPageTitle={setPageTitle} refreshKey={refreshKey} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

// ============================================
// APP
// ============================================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists() && isAllowed(userDoc.data())) {
            const token = await firebaseUser.getIdToken();
            teacherAPI.setToken(token);
            setUser(firebaseUser);
          } else {
            await signOut(auth);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check error:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Refresh token every 30 minutes
  useEffect(() => {
    if (!user) return;
    const refreshToken = async () => {
      try {
        const token = await user.getIdToken(true);
        teacherAPI.setToken(token);
      } catch (error) {
        console.error('Token refresh error:', error);
      }
    };
    const interval = setInterval(refreshToken, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading) {
    return (
      <div className="loading" style={{ height: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  const handleLogin = (firebaseUser) => setUser(firebaseUser);
  const handleLogout = () => {
    setUser(null);
    teacherAPI.setToken(null);
  };

  return (
    <Router>
      {user ? (
        <MainLayout user={user} onLogout={handleLogout} />
      ) : (
        <LoginPage onLogin={handleLogin} />
      )}
    </Router>
  );
}

export default App;
